// ─────────────────────────────────────────────────────────────
//  Academy submission export
//
//  Turns a member's collected submissions (Practice Lab + Mini-Quiz) into a
//  clean, self-contained, print-ready HTML page opened in a new tab. From there
//  the reviewer can "Save as PDF" (or print) and share the file — no extra
//  dependency needed, and the exported text stays real/selectable.
// ─────────────────────────────────────────────────────────────

const PRACTICE_MODE_LABEL = {
  easy: 'Easy Mode',
  hard: 'Hard Mode',
  subject: 'Subject Line Mode',
  salesgod: 'SalesGod Mode',
};

const escapeHtml = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Mirrors describeSubmission in AcademyMembers.js so the export header reads the
// same as the on-screen inbox.
const describeSubmission = (s) => {
  if (s.type === 'quiz') return { tag: 'Mini-Quiz', subject: `Quiz · ${s.lessonTitle}` };
  const mode = s.submission?.mode;
  const tag = `Practice Lab · ${PRACTICE_MODE_LABEL[mode] || ''}`.trim();
  if (mode === 'easy' || mode === 'hard') {
    return { tag, subject: s.submission.subject?.trim() || '(Kein Betreff)' };
  }
  if (mode === 'subject') return { tag, subject: 'Betreffzeilen-Übung' };
  if (mode === 'salesgod') return { tag, subject: 'SalesGod Vorlage' };
  return { tag, subject: 'Einreichung' };
};

// Renders the body of a single submission to HTML (mirror of SubmissionView).
const renderSubmissionBody = (entry) => {
  const sub = entry.submission || {};

  if (entry.type === 'quiz') {
    const answers = sub.answers || [];
    if (answers.length === 0) return '<p class="muted">Keine Antworten erfasst.</p>';
    return answers
      .map((a, i) => {
        const badge =
          a.correct === true
            ? '<span class="badge ok">Correct</span>'
            : a.correct === false
            ? '<span class="badge bad">Wrong</span>'
            : '';
        let answer = '';
        if (a.type === 'multiple-choice') {
          answer = `<p class="answer">${escapeHtml(a.choiceText || `Option ${(a.choiceIndex ?? 0) + 1}`)}</p>`;
        } else if (a.type === 'text') {
          answer = `<p class="answer pre">${escapeHtml(a.text)}</p>`;
        } else if (a.type === 'email') {
          answer =
            `<p class="answer"><strong>Subject:</strong> ${escapeHtml(a.subject)}</p>` +
            `<p class="answer pre">${escapeHtml(a.body)}</p>`;
        }
        return (
          `<div class="qa"><div class="qa-head"><span class="qa-no">${i + 1}</span>` +
          `<span class="qa-q">${escapeHtml(a.question)}</span>${badge}</div>${answer}</div>`
        );
      })
      .join('');
  }

  const mode = sub.mode;
  if (mode === 'easy') {
    return (sub.blocks || [])
      .map(
        (b) =>
          `<div class="block"><span class="block-label">${escapeHtml(b.label)}</span>` +
          `<p class="answer pre">${escapeHtml(b.value || '—')}</p></div>`
      )
      .join('');
  }
  if (mode === 'hard') return `<p class="answer pre">${escapeHtml(sub.body || '—')}</p>`;
  if (mode === 'subject') {
    return (
      '<ol class="subjects">' +
      (sub.subjects || []).map((s) => `<li>${escapeHtml(s || '—')}</li>`).join('') +
      '</ol>'
    );
  }
  if (mode === 'salesgod') return `<pre class="template">${escapeHtml(sub.template || '—')}</pre>`;
  return '<p class="muted">Kein darstellbarer Inhalt.</p>';
};

const renderSubmissionCard = (entry, member) => {
  const { tag, subject } = describeSubmission(entry);
  const path = [entry.topicTitle, entry.courseTitle, entry.lessonTitle]
    .filter(Boolean)
    .map(escapeHtml)
    .join(' &rsaquo; ');
  return `
    <article class="mail">
      <div class="mail-bar">
        <span class="tag ${entry.type === 'quiz' ? 'tag-quiz' : ''}">${escapeHtml(tag)}</span>
        <span class="date">${escapeHtml(fmtDateTime(entry.submittedAt))}</span>
      </div>
      <h2 class="subject">${escapeHtml(subject)}</h2>
      <div class="meta">
        <span class="from"><strong>${escapeHtml(member.name || 'Mitglied')}</strong>${
          member.email ? ` &lt;${escapeHtml(member.email)}&gt;` : ''
        }</span>
        ${path ? `<span class="path">${path}</span>` : ''}
      </div>
      <div class="body">${renderSubmissionBody(entry)}</div>
    </article>`;
};

const buildHtml = (member, submissions) => {
  const title = `Einreichungen · ${member.name || member.id || 'Academy Member'}`;
  const cards = submissions.map((s) => renderSubmissionCard(s, member)).join('');
  const count = submissions.length;
  const generated = new Date().toLocaleString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #14141a;
    background: #f3f4f6;
    line-height: 1.55;
  }
  .sheet { max-width: 820px; margin: 0 auto; padding: 40px 28px 80px; }
  .toolbar {
    position: sticky; top: 0; z-index: 5;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    background: #ffffff; border: 1px solid #e2e4e9; border-radius: 12px;
    padding: 12px 16px; margin-bottom: 28px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.06);
  }
  .toolbar p { margin: 0; font-size: 13px; color: #6b7280; }
  .print-btn {
    border: none; border-radius: 9px; background: #2563eb; color: #fff;
    font-size: 14px; font-weight: 600; padding: 10px 18px; cursor: pointer;
  }
  .print-btn:hover { background: #1d4ed8; }
  .doc-head { margin-bottom: 28px; }
  .doc-head h1 { margin: 0 0 6px; font-size: 26px; font-weight: 800; }
  .doc-head .sub { margin: 0; font-size: 14px; color: #6b7280; }
  .mail {
    background: #ffffff; border: 1px solid #e2e4e9; border-radius: 14px;
    padding: 22px 24px; margin-bottom: 22px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    page-break-inside: avoid;
  }
  .mail-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
  .tag {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
    padding: 3px 10px; border-radius: 7px; background: #e0edff; color: #1d4ed8;
  }
  .tag-quiz { background: #ece8ff; color: #6d4ed4; }
  .date { font-size: 12px; color: #9ca3af; }
  .subject { margin: 0 0 10px; font-size: 19px; font-weight: 700; }
  .meta { display: flex; flex-direction: column; gap: 2px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid #eceef2; }
  .from { font-size: 13.5px; color: #14141a; }
  .path { font-size: 12.5px; color: #6b7280; }
  .body { font-size: 14px; color: #1f2430; }
  .answer { margin: 0 0 8px; }
  .pre { white-space: pre-wrap; word-break: break-word; }
  .block { margin-bottom: 14px; }
  .block-label { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; margin-bottom: 3px; }
  .subjects { margin: 0; padding-left: 22px; }
  .subjects li { margin-bottom: 4px; }
  .template {
    margin: 0; padding: 14px 16px; background: #0f172a; color: #dbeafe;
    border-radius: 9px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px; line-height: 1.6; white-space: pre-wrap; word-break: break-word;
  }
  .qa { border-left: 2px solid #d6d9e0; padding-left: 14px; margin-bottom: 16px; }
  .qa-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
  .qa-no { width: 20px; height: 20px; flex: 0 0 20px; border-radius: 50%; background: #eef0f4; color: #6b7280; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; }
  .qa-q { font-size: 13.5px; font-weight: 600; }
  .badge { font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; }
  .badge.ok { background: #dcfce7; color: #15803d; }
  .badge.bad { background: #fee2e2; color: #b91c1c; }
  .muted { color: #9ca3af; font-style: italic; }
  .empty { text-align: center; padding: 60px 20px; color: #9ca3af; }
  @media print {
    body { background: #ffffff; }
    .sheet { padding: 0; max-width: none; }
    .toolbar { display: none; }
    .mail { box-shadow: none; border-color: #d4d7de; }
  }
</style>
</head>
<body>
  <div class="sheet">
    <div class="toolbar">
      <p>Tipp: &bdquo;Als PDF speichern&ldquo; im Druckdialog wählen, um die Datei zu teilen.</p>
      <button type="button" class="print-btn" onclick="window.print()">Als PDF speichern / Drucken</button>
    </div>
    <div class="doc-head">
      <h1>${escapeHtml(member.name || member.id || 'Academy Member')}</h1>
      <p class="sub">${count} ${count === 1 ? 'Einreichung' : 'Einreichungen'} · Practice Lab &amp; Mini-Quiz · Exportiert am ${escapeHtml(generated)}</p>
    </div>
    ${count ? cards : '<p class="empty">Keine Einreichungen vorhanden.</p>'}
  </div>
</body>
</html>`;
};

// Opens the printable export in a new tab. Returns false if the popup was
// blocked so the caller can surface a hint.
export const exportMemberSubmissions = (member, submissions) => {
  const html = buildHtml(member || {}, submissions || []);
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  return true;
};

export default exportMemberSubmissions;
